/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useState } from 'react';

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Tooltip,
	Avatar,
	Button,
	Stack,
} from '@mantine/core';

// Utils
import { truncateString } from '@utils/functions';

// Components
import { Onglet } from '@components/atoms/Onglet';
import Table from './components/Table/Table';

// Style
import classes from './DrawerOrganization.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import OrganizationLogo from '@assets/images/organization-logo.svg';
import { NOT_APPLICABLE } from '@config/constants/common';
import { TableProducersProjects } from './components/TableAdditionalityProjects/TableProducersProjects';

// Icons
import {
	IconEdit,
	IconQuestionMark,
	IconTrash,
} from '@tabler/icons-react';
import { VerifiedButton } from '@components/atoms';
import { getCountryName } from '@utils/countries';
import isNil from 'lodash/isNil';
import { TParsedOrganization } from '../../types';
import { useGetOrganization } from '../../api/get-organization-by-id';
import { ACLS } from '@config/constants/acls';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TDrawerOrganizationProps = {
	opened: boolean;
	onClose: () => void;
	handledelete: () => void;
	handleupdate: (row: TParsedOrganization) => void;
	data: TParsedOrganization;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerOrganization = (
	props: TDrawerOrganizationProps
) => {
	/* ******************************** PROPS ******************************* */

	const { opened, onClose, handledelete } = props;

	/* ******************************** STATES ******************************* */
	const [activeTab, setActiveTab] = useState(
		'generalInformations'
	);

	const {
		name,
		createdAt,
		updatedAt,
		verifiedBy,
		verifiedAt,
		ownerName,
		ownerEmail,
		ownerPhone,
		location,
		verified,
		logo,
	} = props.data;
	/* ******************************** HELPERS ******************************* */
	const handleCloseDrawerShowMore = () => {
		setActiveTab('generalInformations');
		onClose();
	};

	/* ******************************** HOOKS ******************************* */

	const { data: organizationData, brands } =
		useGetOrganization(props?.data?.id);

	const { t } = useTranslation();

	/* ******************************** CONSTANTS ******************************* */

	const ongletItems = [
		{
			label: t('global.generalInformations'),
			value: 'generalInformations',
			id: 'id-generalInformations',
			nbr: null,
		},
		{
			label: t('global.details'),
			value: 'measurabilitiesProjects',
			id: '2',
			nbr: null,
		},
	];

	const { checkAcl, checkRole } = useAuthorization();
	/* ****************************** RENDERING ****************************** */

	if (props.data)
		return (
			<NativeDrawer
				onClose={handleCloseDrawerShowMore}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				position={'right'}
			>
				<Box className={classes.wrapper}>
					{/* Header */}
					<Box className={classes.customHeader}>
						<Box className={classes.containerLogo}>
							<Image
								className={
									logo
										? classes.logoPicture
										: classes.logo
								}
								src={logo ? logo : OrganizationLogo}
							/>
						</Box>
						<Box className={classes.containerTitle}>
							<Text className={classes.customSubTitle}>
								{t('global.organization')}
							</Text>
							{props.data.name && (
								<Text className={classes.customTitle}>
									{name}
								</Text>
							)}
						</Box>
					</Box>
					<Box className={classes.containerOnglet}>
						<Onglet
							items={ongletItems}
							onChange={(data: string | null) => {
								// eslint-disable-next-line @typescript-eslint/no-unused-expressions
								data !== null && setActiveTab(data);
							}}
						/>
					</Box>
					{/* Body */}
					{activeTab == 'generalInformations' && (
						<>
							<Box className={classes.bodyDrawer}>
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.status')}
									</Text>

									<VerifiedButton
										readOnly
										data-testid={verified + '-testid'}
										isverified={
											verified
												? verified.toString()
												: undefined
										}
									>
										{verified
											? t('global.verified')
											: t('global.notVerified')}
									</VerifiedButton>
								</Box>
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.verifiedAt')}
									</Text>
									{verifiedBy !== NOT_APPLICABLE ? (
										<Text className={classes.value}>
											{verifiedAt}
										</Text>
									) : (
										<Text className={classes.label}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>

								{verifiedBy && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.verifiedBy')}
										</Text>

										{verifiedBy !== NOT_APPLICABLE ? (
											<Tooltip
												label={verifiedBy}
												disabled={verifiedBy.length <= 20}
											>
												<Text className={classes.value}>
													{truncateString(verifiedBy, 20)}
												</Text>
											</Tooltip>
										) : (
											<Text className={classes.label}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								)}
								{!isNil(location) && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.location')}
										</Text>
										<Box
											className={
												classes.groupCountryInformations
											}
										>
											<Avatar
												className={classes.logoCountry}
												radius={'xl'}
												src={`https://flagcdn.com/w40/${location?.countryCode?.toLowerCase()}.png`}
												size={'xs'}
											>
												<IconQuestionMark />
											</Avatar>
											<Tooltip
												label={`${getCountryName(location?.countryCode || '')} / ${location.place}`}
												disabled={
													location.countryCode &&
													location.place &&
													`${location.place}`.length > 10
														? false
														: true
												}
											>
												<Box
													className={
														classes.groupCountryTextInformations
													}
												>
													<Text
														className={
															classes.textCountryProject
														}
													>
														{getCountryName(
															location?.countryCode || ''
														)}
													</Text>

													<Text
														className={
															classes.textCountryPlaceProject
														}
													>
														{`/ ${location.place ? truncateString(location.place, 10) : NOT_APPLICABLE}`}
													</Text>
												</Box>
											</Tooltip>
										</Box>
									</Box>
								)}

								{ownerName && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.ownerName')}
										</Text>

										{ownerName !== NOT_APPLICABLE ? (
											<Tooltip
												label={ownerName}
												disabled={ownerName.length <= 20}
											>
												<Text className={classes.value}>
													{truncateString(ownerName, 20)}
												</Text>
											</Tooltip>
										) : (
											<Text className={classes.label}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								)}

								{ownerEmail && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.ownerEmail')}
										</Text>

										{ownerEmail !== NOT_APPLICABLE ? (
											<Tooltip
												label={ownerEmail}
												disabled={ownerEmail.length <= 20}
											>
												<Text className={classes.value}>
													{truncateString(ownerEmail, 20)}
												</Text>
											</Tooltip>
										) : (
											<Text className={classes.label}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								)}

								{ownerPhone && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.ownerPhone')}
										</Text>
										{ownerPhone !== NOT_APPLICABLE ? (
											<Tooltip
												label={ownerPhone}
												disabled={ownerPhone.length <= 20}
											>
												<Text className={classes.value}>
													{truncateString(ownerPhone, 20)}
												</Text>
											</Tooltip>
										) : (
											<Text className={classes.label}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								)}

								{createdAt && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.createdAt')}
										</Text>
										<Text className={classes.value}>
											{createdAt}
										</Text>
									</Box>
								)}
								{updatedAt && (
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.updatedAt')}
										</Text>
										<Text className={classes.value}>
											{updatedAt}
										</Text>
									</Box>
								)}
							</Box>
						</>
					)}
					{activeTab == 'measurabilitiesProjects' && (
						<Box className={classes.containerTabSecond}>
							<Box className={classes.row}>
								<Text className={classes.title}>
									{t('global.affiliatedBrands')}
								</Text>
							</Box>
							<Box style={{ height: '20px' }} />
							{/* Mesurability projects */}
							<Table rows={brands} />
							<Box style={{ height: '40px' }} />
							<Box className={classes.row}>
								<Text className={classes.title}>
									{t('global.affiliatedProducers')}
								</Text>
							</Box>
							<Box style={{ height: '20px' }} />

							{/* Additionality projects */}
							<TableProducersProjects
								rows={organizationData?.producers}
							/>
						</Box>
					)}
					<Box className={classes.spacer} />
					<Stack gap={'15px'}>
						{checkAcl(ACLS.ORGANIZATIONS.UPDATE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Box className={classes.containerButton}>
									<Button
										leftSection={<IconEdit size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										onClick={() =>
											props.handleupdate(props.data)
										}
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t(
											'organizations.updateOrganization'
										)}
									</Button>
									<Button
										leftSection={<IconEdit size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										onClick={() =>
											props.handleupdate(props.data)
										}
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t(
											'organizations.updateOrganization'
										)}
									</Button>
								</Box>
							)}
						{checkAcl(ACLS.ORGANIZATIONS.DELETE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Box className={classes.containerButton}>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t(
											'organizations.deleteOrganization'
										)}
									</Button>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t(
											'organizations.deleteOrganization'
										)}
									</Button>
								</Box>
							)}
					</Stack>
				</Box>
			</NativeDrawer>
		);
};
